import React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import {
  Box,
  Popover,
  Paper,
  Typography,
  IconButton,
  TextField,
  ButtonBase,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ArrowRightAltRoundedIcon from "@mui/icons-material/ArrowRightAltRounded";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckIcon from "@mui/icons-material/Check";
import { useTimePresets } from '../../hooks/useTimePresets';

/**
 * ReusableTimePickerPopover
 * -------------------------
 * Matches your screenshot:
 * - Title "Time" with close button
 * - Start time input  (outlined coral when focused)
 * - Arrow -> end time input
 * - Timezone picker icon to the LEFT of end time input
 * - "Duration" row:
 *   • Hour pill (dropdown)
 *   • Minute pill (dropdown)
 * - "Presets" grid of duration pills (15m default highlighted)
 *
 * Integration:
 * - You anchor it to your time row inside Add Task modal (like your Date picker)
 * - It returns: startTime, endTime, durationMinutes, timeZone
 */

export function ReusableTimePickerPopover({
  value,
  onChange,
  open,
  anchorEl,
  onClose,
  defaultPresetMinutes = 15,
  availableTimeZones = [
    "Australia/Brisbane",
    "Australia/Sydney",
    "Australia/Melbourne",
    "UTC",
  ],
  user, // Add user prop for Firebase integration
}) {
  const { tokens } = useAppTheme();
  const muted = "rgb(133, 133, 133)";
  const textDark = "rgb(20, 20, 20)";
  const border = "1px solid rgba(0,0,0,0.06)";
  const soft = "rgba(0,0,0,0.03)";

  const { presets, loading: presetsLoading, error: presetsError, addPreset, updatePreset, deletePreset, reorderPresets } = useTimePresets(user);

  // State for preset editing dialog
  const [presetDialogOpen, setPresetDialogOpen] = React.useState(false);
  const [editingPreset, setEditingPreset] = React.useState(null);
  const [newPresetLabel, setNewPresetLabel] = React.useState('');
  const [newPresetMinutes, setNewPresetMinutes] = React.useState(15);

  // State for inline preset editing
  const [isEditingPresets, setIsEditingPresets] = React.useState(false);
  const [editablePresets, setEditablePresets] = React.useState([]);

  // Sync editable presets when presets change
  React.useEffect(() => {
    if (!isEditingPresets && presets.length > 0) {
      setEditablePresets(presets.map(p => ({ ...p })));
    }
  }, [presets, isEditingPresets]);

  // local working state (so typing doesn't immediately jerk other fields)
  const [start, setStart] = React.useState(value.start);
  const [end, setEnd] = React.useState(value.end);
  const [timeZone, setTimeZone] = React.useState(value.timeZone);

  const [durH, setDurH] = React.useState(() => Math.floor(defaultPresetMinutes / 60));
  const [durM, setDurM] = React.useState(() => defaultPresetMinutes % 60);

  // menus
  const [tzAnchor, setTzAnchor] = React.useState(null);
  const [hAnchor, setHAnchor] = React.useState(null);
  const [mAnchor, setMAnchor] = React.useState(null);

  // reset on open
  React.useEffect(() => {
    if (!open) return;

    setStart(value.start);
    setEnd(value.end);
    setTimeZone(value.timeZone);

    const d = minutesBetween(value.start, value.end);
    const base = Number.isFinite(d) && d > 0 ? d : defaultPresetMinutes;
    setDurH(Math.floor(base / 60));
    setDurM(base % 60);

    setTzAnchor(null);
    setHAnchor(null);
    setMAnchor(null);
  }, [open, value.start, value.end, value.timeZone, defaultPresetMinutes]);

  // keep duration pills consistent with start/end edits
  React.useEffect(() => {
    const d = minutesBetween(start, end);
    if (Number.isFinite(d) && d >= 0) {
      setDurH(Math.floor(d / 60));
      setDurM(d % 60);
    }
  }, [start, end]);

  function commit(nextStart, nextEnd, nextTz) {
    const durationMinutes = Math.max(0, minutesBetween(nextStart, nextEnd));
    onChange({ start: nextStart, end: nextEnd, timeZone: nextTz, durationMinutes });
  }

  function setDurationMinutes(totalMinutes) {
    const safe = Math.max(0, totalMinutes);
    const nextEnd = addMinutesToTime(start, safe);
    setEnd(nextEnd);
    setDurH(Math.floor(safe / 60));
    setDurM(safe % 60);
    commit(start, nextEnd, timeZone);
  }

  function handleDurationPick(hours, minutes) {
    const total = Math.max(0, hours * 60 + minutes);
    setDurationMinutes(total);
  }

  function handleStartBlur() {
    const norm = normalizeTime(start);
    if (norm) setStart(norm);
    const nextEnd = addMinutesToTime(norm ?? start, durH * 60 + durM);
    setEnd(nextEnd);
    commit(norm ?? start, nextEnd, timeZone);
  }

  function handleEndBlur() {
    const norm = normalizeTime(end);
    if (norm) setEnd(norm);
    commit(start, norm ?? end, timeZone);
  }

  // Preset editing functions
  const handleEditPresets = () => {
    setIsEditingPresets(true);
    setEditablePresets(presets.map(p => ({ ...p })));
  };

  const handleSavePresets = async () => {
    try {
      // Update all presets in the database
      for (const preset of editablePresets) {
        await updatePreset(preset.id, {
          label: preset.label,
          minutes: preset.minutes
        });
      }
      setIsEditingPresets(false);
    } catch (error) {
      console.error('Error saving presets:', error);
    }
  };

  const handlePresetLabelChange = (index, newLabel) => {
    const updatedPresets = [...editablePresets];
    updatedPresets[index] = {
      ...updatedPresets[index],
      label: newLabel
    };
    setEditablePresets(updatedPresets);
  };

  const handleAddPreset = () => {
    setEditingPreset(null);
    setNewPresetLabel('');
    setNewPresetMinutes(15);
  };

  const handleEditPreset = (preset, index) => {
    setEditingPreset({ ...preset, index });
    setNewPresetLabel(preset.label);
    setNewPresetMinutes(preset.minutes);
  };

  const handleSavePreset = () => {
    if (!newPresetLabel.trim()) return;

    const presetData = {
      label: newPresetLabel.trim(),
      minutes: newPresetMinutes,
    };

    if (editingPreset) {
      updatePreset(editingPreset.index, presetData);
    } else {
      addPreset(presetData);
    }

    setPresetDialogOpen(false);
    setEditingPreset(null);
    setNewPresetLabel('');
    setNewPresetMinutes(15);
  };

  const handleDeletePreset = (index) => {
    deletePreset(index);
  };

  const generatePresetLabel = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const currentDuration = durH * 60 + durM;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        setTzAnchor(null);
        setHAnchor(null);
        setMAnchor(null);
        onClose();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        elevation: 0,
        sx: {
          mt: 1,
          borderRadius: 4,
          border,
          boxShadow: "0px 10px 28px rgba(0,0,0,0.14)",
          overflow: "hidden",
        },
      }}
    >
      <Paper sx={{ width: 360, background: "white" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'rgba(20, 20, 20, 0.85)' }}>
            Time
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close time picker"
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              border,
              background: "rgba(0,0,0,0.03)",
              "&:hover": { background: "rgba(0,0,0,0.05)" },
            }}
          >
            <CloseIcon fontSize="small" sx={{ color: muted }} />
          </IconButton>
        </Box>

        {/* Time row */}
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 36px 1fr 44px", alignItems: "center", gap: 1 }}>
            {/* Start */}
            <TimeInput
              value={start}
              onChange={setStart}
              onBlur={handleStartBlur}
              activeOutlineColor={tokens.primary}
              softBg={soft}
            />

            {/* arrow */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ArrowRightAltRoundedIcon sx={{ color: "rgba(0,0,0,0.35)" }} />
            </Box>

            {/* End */}
            <TimeInput
              value={end}
              onChange={setEnd}
              onBlur={handleEndBlur}
              activeOutlineColor={tokens.primary}
              softBg={soft}
            />

            {/* TZ icon (right of end) */}
            <IconButton
              size="small"
              onClick={(e) => setTzAnchor(e.currentTarget)}
              aria-label="Choose time zone"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "white",
                "&:hover": { background: "white" },
              }}
              title={timeZone}
            >
              <PublicOutlinedIcon sx={{ color: "rgba(0,0,0,0.45)" }} fontSize="small" />
            </IconButton>
          </Box>

          {/* TZ menu */}
          <Menu
            anchorEl={tzAnchor}
            open={Boolean(tzAnchor)}
            onClose={() => setTzAnchor(null)}
          >
            {availableTimeZones.map((tz) => (
              <MenuItem
                key={tz}
                selected={tz === timeZone}
                onClick={() => {
                  setTimeZone(tz);
                  setTzAnchor(null);
                  commit(start, end, tz);
                }}
              >
                {tz}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Divider sx={{ borderColor: "rgba(0,0,0,0.05)" }} />

        {/* Duration */}
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'rgba(20, 20, 20, 0.85)', mb: 1 }}>
            Duration
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}>
            <DurationPill
              label={`${durH}h`}
              onClick={(e) => setHAnchor(e.currentTarget)}
            />
            <DurationPill
              label={`${durM}m`}
              onClick={(e) => setMAnchor(e.currentTarget)}
            />
          </Box>

          {/* hour menu */}
          <Menu
            anchorEl={hAnchor}
            open={Boolean(hAnchor)}
            onClose={() => setHAnchor(null)}
          >
            {Array.from({ length: 13 }, (_, i) => i).map((h) => (
              <MenuItem
                key={h}
                selected={h === durH}
                onClick={() => {
                  setHAnchor(null);
                  handleDurationPick(h, durM);
                }}
              >
                {h}h
              </MenuItem>
            ))}
          </Menu>

          {/* minute menu */}
          <Menu
            anchorEl={mAnchor}
            open={Boolean(mAnchor)}
            onClose={() => setMAnchor(null)}
          >
            {[0, 5, 10, 15, 20, 30, 45, 50, 55].map((m) => (
              <MenuItem
                key={m}
                selected={m === durM}
                onClick={() => {
                  setMAnchor(null);
                  handleDurationPick(durH, m);
                }}
              >
                {m}m
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Presets */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'rgba(20, 20, 20, 0.85)' }}>
              Presets
            </Typography>
            <IconButton
              size="small"
              onClick={isEditingPresets ? handleSavePresets : handleEditPresets}
              aria-label={isEditingPresets ? "Save presets" : "Edit presets"}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                "&:hover": { background: "rgba(0,0,0,0.04)" },
              }}
            >
              {isEditingPresets ? (
                <CheckIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.35)" }} />
              ) : (
                <EditIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.35)" }} />
              )}
            </IconButton>
          </Box>

          {presetsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Typography sx={{ fontSize: 12, color: muted }}>Loading presets...</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
              }}
            >
              {(isEditingPresets ? editablePresets : presets).map((p, index) => {
                const isActive = p.minutes === currentDuration;
                return isEditingPresets ? (
                  <TextField
                    key={`${p.label}-${index}`}
                    size="small"
                    value={p.label}
                    onChange={(e) => handlePresetLabelChange(index, e.target.value)}
                    placeholder="Label"
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: 12,
                        padding: "4px 8px",
                      },
                    }}
                  />
                ) : (
                  <PresetPill
                    key={`${p.label}-${index}`}
                    label={p.label}
                    active={isActive}
                    onClick={() => setDurationMinutes(p.minutes)}
                    activeColor={tokens.primary}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Preset Editing Dialog */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 900 }}>
          {editingPreset ? 'Edit Preset' : 'Manage Presets'}
        </DialogTitle>
        <DialogContent>
          {/* Add/Edit Preset Form */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
              {editingPreset ? 'Edit Preset' : 'Add New Preset'}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Label"
                value={newPresetLabel}
                onChange={(e) => setNewPresetLabel(e.target.value)}
                placeholder="e.g., 2h 30m"
                size="small"
              />
              <TextField
                label="Minutes"
                type="number"
                value={newPresetMinutes}
                onChange={(e) => setNewPresetMinutes(Number(e.target.value))}
                placeholder="90"
                size="small"
              />
            </Stack>
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSavePreset}
                disabled={!newPresetLabel.trim()}
                sx={{ backgroundColor: tokens.primary, "&:hover": { backgroundColor: tokens.primaryHover } }}
              >
                {editingPreset ? 'Update' : 'Add'}
              </Button>
              {editingPreset && (
                <Button onClick={() => setEditingPreset(null)}>
                  Cancel
                </Button>
              )}
            </Box>
          </Box>

          {/* Existing Presets */}
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
              Existing Presets
            </Typography>
            <Stack spacing={1}>
              {presets.map((preset, index) => (
                <Box
                  key={`${preset.label}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    border: "1px solid rgba(0,0,0,0.06)",
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DragIndicatorIcon sx={{ fontSize: 16, color: muted }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {preset.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: muted }}>
                      ({preset.minutes}m)
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditPreset(preset, index)}
                      sx={{ width: 24, height: 24 }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeletePreset(index)}
                      sx={{ width: 24, height: 24 }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Popover>
  );
}

/* --------------------------- UI atoms --------------------------- */

function TimeInput({
  value,
  onChange,
  onBlur,
  activeOutlineColor,
  softBg,
}) {
  const [focusedPart, setFocusedPart] = React.useState('hours'); // 'hours' or 'minutes'
  const inputRef = React.useRef(null);

  // Parse current value into hours and minutes
  const parseTime = (timeStr) => {
    const parts = (timeStr || '').split(':');
    return {
      hours: parts[0] || '',
      minutes: parts[1] || ''
    };
  };

  const { hours, minutes } = parseTime(value);

  // Validate and format time input
  const handleTimeChange = (e) => {
    let inputValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Remove any non-digit characters except colon
    inputValue = inputValue.replace(/[^\d:]/g, '');
    
    // Handle colon positioning
    if (inputValue.length === 2 && !inputValue.includes(':')) {
      // Auto-add colon after 2 digits if user hasn't typed it
      inputValue += ':';
    } else if (inputValue.length > 2 && !inputValue.includes(':')) {
      // Insert colon after first 2 digits if user keeps typing
      inputValue = inputValue.slice(0, 2) + ':' + inputValue.slice(2, 4);
    }
    
    // Limit to HH:MM format (max 5 characters)
    if (inputValue.length > 5) {
      inputValue = inputValue.slice(0, 5);
    }
    
    // Validate hours (00-23) and minutes (00-59)
    const parts = inputValue.split(':');
    if (parts.length === 2) {
      let hours = parts[0];
      let minutes = parts[1];
      
      // Limit hours to 0-23
      if (hours.length > 2) {
        hours = hours.slice(0, 2);
      }
      if (hours.length === 2 && parseInt(hours) > 23) {
        hours = '23';
      }
      
      // Limit minutes to 0-59
      if (minutes.length > 2) {
        minutes = minutes.slice(0, 2);
      }
      if (minutes.length === 2 && parseInt(minutes) > 59) {
        minutes = '59';
      }
      
      inputValue = hours + ':' + minutes;
    }
    
    onChange(inputValue);
  };

  // Handle key events for tab navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      
      const parts = parseTime(value);
      
      if (focusedPart === 'hours') {
        // Moving from hours to minutes
        setFocusedPart('minutes');
        
        // If hours are empty, default to current hour or 00
        let newValue = value;
        if (!parts.hours || parts.hours.length === 0) {
          const currentHour = new Date().getHours().toString().padStart(2, '0');
          newValue = `${currentHour}:${parts.minutes || '00'}`;
          onChange(newValue);
        }
        
        // Set cursor to minutes position
        setTimeout(() => {
          if (inputRef.current) {
            const colonIndex = newValue.indexOf(':');
            inputRef.current.setSelectionRange(colonIndex + 1, colonIndex + 3);
          }
        }, 0);
      } else {
        // Moving from minutes to next field (call original onBlur)
        if (onBlur) {
          onBlur(e);
        }
      }
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      // Allow arrow key navigation between hours and minutes
      e.preventDefault();
      const colonIndex = value.indexOf(':');
      
      if (e.key === 'ArrowRight' && focusedPart === 'hours') {
        setFocusedPart('minutes');
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(colonIndex + 1, colonIndex + 3);
          }
        }, 0);
      } else if (e.key === 'ArrowLeft' && focusedPart === 'minutes') {
        setFocusedPart('hours');
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(0, colonIndex);
          }
        }, 0);
      }
    }
  };

  // Handle click to focus on specific part
  const handleClick = (e) => {
    const clickPos = e.target.selectionStart;
    const colonIndex = value.indexOf(':');
    
    if (colonIndex === -1) {
      setFocusedPart('hours');
    } else if (clickPos <= colonIndex) {
      setFocusedPart('hours');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(0, colonIndex);
        }
      }, 0);
    } else {
      setFocusedPart('minutes');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(colonIndex + 1, colonIndex + 3);
        }
      }, 0);
    }
  };

  // Validate and format on blur
  const handleBlur = (e) => {
    let inputValue = e.target.value;
    
    // If input is incomplete, try to format it
    if (inputValue && !inputValue.includes(':')) {
      // If only digits, format as HH:MM
      if (inputValue.length <= 2) {
        // Treat as hours, add :00
        inputValue = inputValue.padStart(2, '0') + ':00';
      } else {
        // Take first 2 digits as hours, rest as minutes
        const hours = inputValue.slice(0, 2).padStart(2, '0');
        const minutes = inputValue.slice(2, 4).padEnd(2, '0');
        inputValue = hours + ':' + minutes;
      }
    }
    
    // Final validation
    const parts = inputValue.split(':');
    if (parts.length === 2) {
      let hours = parseInt(parts[0]) || 0;
      let minutes = parseInt(parts[1]) || 0;
      
      // Clamp values
      hours = Math.min(23, Math.max(0, hours));
      minutes = Math.min(59, Math.max(0, minutes));
      
      inputValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    } else {
      // Invalid format, default to 00:00
      inputValue = '00:00';
    }
    
    onChange(inputValue);
    if (onBlur) onBlur(e);
  };

  return (
    <TextField
      value={value}
      onChange={handleTimeChange}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onBlur={handleBlur}
      placeholder="HH:mm"
      inputRef={inputRef}
      inputProps={{ 
        inputMode: "numeric",
        pattern: "[0-9]{2}:[0-9]{2}",
        maxLength: 5
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: 44,
          borderRadius: 3,
          background: "white",
          "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
          "&:hover fieldset": { borderColor: "rgba(0,0,0,0.10)" },
          "&.Mui-focused fieldset": { borderColor: activeOutlineColor, borderWidth: 2 },
        },
        "& input": {
          fontWeight: 400,
          letterSpacing: 0.4,
          textAlign: "center",
          padding: 0,
        },
      }}
    />
  );
}

function DurationPill({
  label,
  onClick,
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        height: 34,
        px: 1.25,
        borderRadius: 999,
        border: "1px solid rgba(0,0,0,0.06)",
        background: "white",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 400, color: "rgba(0,0,0,0.55)" }}>
        {label}
      </Typography>
      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.35)" }} />
    </ButtonBase>
  );
}

function PresetPill({
  label,
  active,
  onClick,
  activeColor,
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        height: 34,
        borderRadius: 999,
        border: active ? `1px solid rgba(244,159,153,0.65)` : "1px solid rgba(0,0,0,0.06)",
        background: active ? "rgba(244,159,153,0.20)" : "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 400,
          color: active ? activeColor : "rgba(0,0,0,0.55)",
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

/* --------------------------- Time math helpers --------------------------- */

function normalizeTime(raw) {
  // Accept "HH:mm" or "H:mm" or "HHmm" or "H"
  const s = raw.trim();
  if (!s) return null;

  // If it's HHmm (e.g. "2200")
  const hhmm = s.match(/^(\d{1,2})(\d{2})$/);
  if (hhmm) {
    const h = Number(hhmm[1]);
    const m = Number(hhmm[2]);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return `${pad2(h)}:${pad2(m)}`;
  }

  // If it's H or HH
  const hhOnly = s.match(/^(\d{1,2})$/);
  if (hhOnly) {
    const h = Number(hhOnly[1]);
    if (h >= 0 && h <= 23) return `${pad2(h)}:00`;
  }

  // If it's H:mm or HH:mm
  const hm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (hm) {
    const h = Number(hm[1]);
    const m = Number(hm[2]);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return `${pad2(h)}:${pad2(m)}`;
  }

  return null;
}

function minutesBetween(start, end) {
  const s = parseTimeToMinutes(start);
  const e = parseTimeToMinutes(end);
  if (s == null || e == null) return NaN;
  return e - s;
}

function parseTimeToMinutes(t) {
  const norm = normalizeTime(t);
  if (!norm) return null;
  const [h, m] = norm.split(":").map(Number);
  return h * 60 + m;
}

function addMinutesToTime(start, minutes) {
  const s = parseTimeToMinutes(start);
  if (s == null) return start;
  const total = (s + minutes) % (24 * 60);
  const safe = total < 0 ? total + 24 * 60 : total;
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}
